import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useAuth } from "../Auth/AuthContext";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";

const PublisherHome = () => {
  const { authState } = useAuth();
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    if (inView) controls.start("visible");
  }, [controls, inView]);

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { 
        type: "spring",
        damping: 10,
        stiffness: 100,
        duration: 0.6 
      }
    },
    hover: {
      y: -5,
      scale: 1.02,
      transition: { duration: 0.2 }
    }
  };

  // Stats data
  const stats = [
    { value: "87%", label: "Fill Rate", change: "+2.1%", color: "from-purple-500 to-indigo-500" },
    { value: "$4.21", label: "RPM", change: "+0.34", color: "from-blue-500 to-cyan-500" },
    { value: "1.2M", label: "Impressions", change: "12.4%", color: "from-green-500 to-teal-500" },
    { value: "42s", label: "Avg. Session", change: "-3.2s", color: "from-amber-500 to-orange-500" }
  ];

  const dummyRevenueData = [
    { day: "Mon", revenue: 120 },
    { day: "Tue", revenue: 200 },
    { day: "Wed", revenue: 180 },
    { day: "Thu", revenue: 250 },
    { day: "Fri", revenue: 300 },
    { day: "Sat", revenue: 400 },
    { day: "Sun", revenue: 350 },
  ];

  const performanceTips = [
    {
      title: "Enable auto-refresh",
      description: "Can increase impressions by 15-20%",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: "text-purple-600 bg-purple-100"
    },
    {
      title: "Test different ad sizes",
      description: "300x250 often performs best",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      ),
      color: "text-blue-600 bg-blue-100"
    },
    {
      title: "Optimize ad placement",
      description: "Above-the-fold performs 60% better",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
      color: "text-green-600 bg-green-100"
    }
  ];

  const recentActivities = [
    {
      title: "New payment processed",
      amount: "$1,245.00",
      time: "2 hours ago",
      icon: "💰"
    },
    {
      title: "Performance alert",
      description: "RPM increased by 12%",
      time: "5 hours ago",
      icon: "📈"
    },
    {
      title: "New ad unit created",
      description: "Size: 300x250",
      time: "1 day ago",
      icon: "🆕"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50">
      {/* Animated background elements - more refined */}
      <div className="fixed inset-0 overflow-hidden z-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 opacity-20"
            style={{
              width: Math.random() * 200 + 50,
              height: Math.random() * 200 + 50,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, Math.random() * 100 - 50],
              x: [0, Math.random() * 100 - 50],
              opacity: [0.1, 0.2, 0.1],
              rotate: [0, Math.random() * 360],
            }}
            transition={{
              duration: Math.random() * 30 + 20,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header with more elegant animation */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ 
            type: "spring",
            damping: 10,
            stiffness: 100,
            duration: 0.8 
          }}
          className="mb-16 text-center"
        >
          <motion.h1 
            className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-3"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            Publisher Dashboard
          </motion.h1>
          <motion.p 
            className="text-xl text-gray-600 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Insights and tools to maximize your ad revenue
          </motion.p>
        </motion.div>

        {/* Stats Cards with gradient backgrounds */}
        <motion.div
          ref={ref}
          variants={container}
          initial="hidden"
          animate={controls}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              variants={item}
              whileHover="hover"
              className={`bg-gradient-to-br ${stat.color} rounded-xl shadow-lg p-6 text-white`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-opacity-80 text-sm font-medium">{stat.label}</p>
                  <p className="text-3xl font-bold text-white mt-1">{stat.value}</p>
                </div>
                <motion.div 
                  className={`px-3 py-1 rounded-md text-xs font-semibold backdrop-blur-sm bg-white bg-opacity-20 ${
                    stat.change.startsWith('+') ? 'text-green-100' : 'text-red-100'
                  }`}
                  whileHover={{ scale: 1.05 }}
                >
                  {stat.change}
                </motion.div>
              </div>
              <div className="mt-4 h-2 bg-white bg-opacity-30 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full ${stat.change.startsWith('+') ? 'bg-green-200' : 'bg-red-200'}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.random() * 60 + 40}%` }}
                  transition={{ 
                    delay: 0.5, 
                    duration: 1.5,
                    type: "spring",
                    damping: 10
                  }}
                />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Revenue Chart with area gradient */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ 
              delay: 0.4,
              type: "spring",
              stiffness: 100
            }}
            className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6 border border-gray-100 backdrop-blur-sm bg-opacity-70"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Revenue Overview</h2>
              <motion.select 
                className="bg-gray-100 border-0 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                whileHover={{ scale: 1.02 }}
                whileFocus={{ scale: 1.02 }}
              >
                <option>Last 7 days</option>
                <option>Last 30 days</option>
                <option>Last 90 days</option>
              </motion.select>
            </div>
            <div className="h-64 rounded-lg">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dummyRevenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#7c3aed" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="day" 
                    tick={{ fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis 
                    tick={{ fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <Tooltip 
                    contentStyle={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      backdropFilter: 'blur(4px)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#7c3aed" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorRevenue)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Right sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ 
              delay: 0.6,
              type: "spring",
              stiffness: 100
            }}
            className="space-y-6"
          >
            {/* Publicity Highlights */}
            <motion.div
              className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 backdrop-blur-sm bg-opacity-70"
              whileHover={{ y: -2 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <h2 className="text-2xl font-bold text-gray-800 text-center mb-4">
                Why Publishers <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">Trust Us</span>
              </h2>
              <ul className="space-y-4 text-gray-700">
                <motion.li 
                  className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  whileHover={{ x: 5 }}
                >
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                      <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="font-medium">Over <span className="text-purple-600 font-bold">500+ publishers</span> onboarded worldwide</p>
                  </div>
                </motion.li>
                <motion.li 
                  className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  whileHover={{ x: 5 }}
                >
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="font-medium">More than <span className="text-green-600 font-bold">$2M in revenue</span> generated monthly</p>
                  </div>
                </motion.li>
                <motion.li 
                  className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  whileHover={{ x: 5 }}
                >
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="font-medium">Delivering <span className="text-blue-600 font-bold">300M+ monthly ad impressions</span></p>
                  </div>
                </motion.li>
              </ul>
              <motion.p 
                className="text-center text-sm text-gray-500 mt-6 italic"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                Join the fastest-growing monetization network and maximize your ad potential.
              </motion.p>
            </motion.div>

            {/* Performance Tips */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 backdrop-blur-sm bg-opacity-70"
              whileHover={{ y: -2 }}
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4">Performance Tips</h2>
              <div className="space-y-4">
                {performanceTips.map((tip, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="flex-shrink-0">
                      <div className={`w-10 h-10 rounded-full ${tip.color.split(' ')[1]} flex items-center justify-center`}>
                        <div className={tip.color.split(' ')[0]}>
                          {tip.icon}
                        </div>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-800">{tip.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{tip.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Recent Activity with emoji icons */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            delay: 1,
            type: "spring",
            stiffness: 100
          }}
          className="mt-16 bg-white rounded-xl shadow-lg p-6 border border-gray-100 backdrop-blur-sm bg-opacity-70"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-6">Recent Activity</h2>
          <div className="space-y-4">
            {recentActivities.map((activity, index) => (
              <motion.div 
                key={index}
                className="flex items-start pb-4 border-b border-gray-100 last:border-0 group"
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="flex-shrink-0 mt-1">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-purple-100 transition-colors">
                    <span className="text-lg">{activity.icon}</span>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-800">
                    {activity.title} {activity.amount && <span className="text-purple-600">{activity.amount}</span>}
                  </p>
                  {activity.description && (
                    <p className="text-xs text-gray-500 mt-1">{activity.description}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.button
            className="mt-6 w-full py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
          >
            View All Activity
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default PublisherHome;